import { BrokerGroupSelect } from '@/components/selects/BrokerGroupSelect';
import { OwnerAutocomplete } from '@/components/selects/OwnerSelect';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Stack,
	Switch,
	Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ShareField } from './ShareField';

const ShareDialog = ({
	title,
	onConfirm = (userIds, brokerGroupIds, ledger) => {},
	config = {},
	hideUsers = false,
	hideBrokerGroups = false,
	hideLedger = true,
	showAdmin = false,
}) => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email });

	const open = useSelector((state) => state.dialog.open?.share);
	const data = useSelector((state) => state.dialog.data?.share);
	const [userIds, setUserIds] = useState(data?.userIds ?? []);
	const [brokerGroupIds, setBrokerGroupIds] = useState(data?.brokerGroupIds ?? []);
	const [sharedWithLedger, setSharedWithLedger] = useState(!hideLedger && Boolean(data?.ledger));

	useEffect(() => setUserIds(data?.userIds ?? []), [data?.userIds]);
	useEffect(() => setBrokerGroupIds(data?.brokerGroupIds ?? []), [data?.brokerGroupIds]);
	useEffect(() => setSharedWithLedger(!hideLedger && Boolean(data?.ledger)), [data?.ledger]);

	const handleClose = () => {
		dispatch(closeDialog('share'));
	};

	const isLoading = false;
	const isSubmitting = false;

	const handleUserChange = (value) => {
		setUserIds(value);
	};
	const handleBrokerChange = (value) => {
		setBrokerGroupIds(value);
	};

	const handleConfirm = () => {
		onConfirm(userIds, brokerGroupIds, hideLedger ? null : sharedWithLedger);
		handleClose();
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={() => {
					handleClose();
				}}
				hideBackdrop={true}
				slotProps={{
					paper: {
						sx: {
							minWidth: { xs: '100%', sm: '70%', md: '50%' },
						},
					},
				}}
			>
				<DialogTitle>{`Share ${title}`}</DialogTitle>
				<DialogContent sx={{ paddingBottom: '2em' }}>
					<Stack spacing={2}>
						{!hideUsers && (
							<ShareField label='Share with users'>
								<OwnerAutocomplete
									multiple
									userId={userIds}
									onChange={handleUserChange}
									isUserFixed={(user) => {
										return user?.id === userQuery.data?.userId;
									}}
									disabled={sharedWithLedger}
									{...(config.user ?? {})}
								/>
							</ShareField>
						)}
						{!hideBrokerGroups && (
							<ShareField
								label='Share with groups'
								helperText={`This will share this ${title?.toLowerCase()} with all users in the selected group`}
							>
								<BrokerGroupSelect
									brokerGroupId={brokerGroupIds}
									onChange={handleBrokerChange}
									multiple={true}
									disabled={sharedWithLedger}
									isGroupDisabled={(group) => sharedWithLedger}
									isGroupFixed={(group) => sharedWithLedger}
									showAdmin={showAdmin}
									{...(config.brokerGroup ?? {})}
								/>
							</ShareField>
						)}
						{!hideLedger && (
							<Stack>
								<FormLabel>{'Share with ledger'}</FormLabel>
								<FormHelperText sx={{ marginLeft: 0 }}>
									{`This will share this ${title?.toLowerCase()} with all users in your ledger`}
								</FormHelperText>

								<FormControlLabel
									control={
										<Switch
											checked={sharedWithLedger}
											onChange={(e) => setSharedWithLedger(e.target.checked)}
											{...(config.ledger?.switch ?? {})}
										/>
									}
									label={`Share${sharedWithLedger ? 'd' : ''} with ledger`}
									slotProps={{
										typography: {
											...(sharedWithLedger && { color: theme.palette.primary.main }),
										},
									}}
									{...(config.ledger?.label ?? {})}
								/>
							</Stack>
						)}
						{sharedWithLedger && !data?.ledger && (
							<Typography>{`Sharing this ${title?.toLowerCase()} with the entire ledger will override any group or user-based settings.`}</Typography>
						)}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={(e) => {
							e.preventDefault();
							handleClose();
						}}
						color='primary'
					>
						Cancel
					</Button>
					<Button
						color='primary'
						variant='contained'
						disabled={isLoading || isSubmitting}
						onClick={handleConfirm}
					>
						{isSubmitting ? (
							<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
						) : (
							'Share'
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ShareDialog;
